import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowPathIcon,
  BookOpenIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { getPassages } from "../../services/passageService";
import TableWithServerOrdering from "../../components/Table/TableWithServerOrdering";
import Select from "../../components/forms/Select/Select";
import { filterSelectStyles } from "../../components/forms/Select/styles";
import BankTabs, { markPassagesAvailable } from "../../components/Passages/BankTabs";
import PassageDialog from "../../components/Passages/PassageDialog";
import useBankLists from "../../components/Passages/useBankLists";
import FolderBar from "../../components/QuestionFolders/FolderBar";
import ShareFolderDialog from "../../components/QuestionFolders/ShareFolderDialog";
import FilterPanel from "../../components/QuestionList/FilterPanel";
import ResultsBar, { Pager } from "../../components/QuestionList/ResultsBar";
import { SubjectCell } from "../../components/QuestionList/cells";
import { HelpTitle } from "../../components/Help/HelpButton";
import TextsGuide from "../../components/Help/guides/TextsGuide";
import { canCreateIn, folderPath, folderTree, isAdminUser, isSubjectFolder } from "../../utils/folders";
import { canOpenPage } from "../../utils/permissions";
import { UserContext } from "../../utils/contexts/UserContext";
import { isCanceledError } from "../../utils/examError";
import { getFilterState, setFilterState } from "../../utils/filterState";
import { formatDate } from "../../utils/formatDate";
import { isOpenClick } from "../../utils/rowLink";
import { cardClass, focusRing, linkButtonClass, useMediaQuery } from "../Exams/TestBuilder/ui";

const PAGE_SIZE = 50;
const ALL = "all";

const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);
const filterValue = (value) => (value !== ALL ? value : undefined);
const optionLabel = (options, value) => options.find((o) => o.value === value)?.label || "";

const primaryButton = `inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 ${focusRing}`;

/**
 * The Texts tab of the question bank: passages (shared texts, tables, charts) with the same folder card as the
 * Questions tab (the open folder goes along when switching tabs), search, subject filter and server paging.
 * A passage opens in PassageDialog, also from a link: ?open=<uuid> (the passage chips of the question
 * tables). Whoever can create somewhere (global rights, or a subject role that creates) makes new ones here;
 * inside a subject the new passage starts in it.
 * `mine`: the Texts tab of My Questions, only the passages the user created.
 */
export default function Passages({ mine = false }) {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const { user } = useContext(UserContext);
  const myUuid = user?.uuid;
  const perms = user?.perms || [];
  const isAdmin = isAdminUser(user);
  const hasGlobalWrite = perms.includes("create_question") || perms.includes("create_exam");
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get("open");
  // Phones get the passages as cards instead of the table.
  const wide = useMediaQuery("(min-width: 768px)");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [subject, setSubject] = useState(ALL);
  const [ordering, setOrdering] = useState("-updated");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The same lists as the passage form of the dialog (loaded once, shared).
  const { subjects, folders, fresh } = useBankLists();
  // uuid null = a new passage (in `folder`, a subject, or chosen in the form). The uuid stays while the
  // dialog closes (its leave transition).
  const [dialog, setDialog] = useState({ open: false, uuid: null, folder: null });
  const [shareDialog, setShareDialog] = useState({ open: false, folder: null });

  // ALL, "null" (No folder), an exam's or a subject's uuid. The bank keeps it in the address (?folder=), as its
  // Questions tab does; My Questions keeps it in the shared filters, as its Questions tab does.
  const [mineFolder, setMineFolder] = useState(() => (mine ? getFilterState().folder || ALL : ALL));
  const folder = mine ? mineFolder : searchParams.get("folder") || ALL;
  const setFolder = useCallback(
    (value) => {
      const next = value || ALL;
      setPage(1);
      if (mine) {
        setMineFolder(next);
        return;
      }
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === ALL) params.delete("folder");
          else params.set("folder", next);
          return params;
        },
        { replace: true }
      );
    },
    [mine, setSearchParams]
  );

  const { byUuid } = useMemo(() => folderTree(folders), [folders]);
  const currentSubject = isSubjectFolder(byUuid.get(folder)) ? byUuid.get(folder) : null;
  const canCreate = hasGlobalWrite || isAdmin || folders.some((f) => canCreateIn(f.role));

  // A folder the user can not see (an old link, a deleted folder) falls back to all passages. Checked against
  // the lists of this visit only: the cached ones may predate a folder just created.
  useEffect(() => {
    if (fresh && folder !== ALL && folder !== "null" && !byUuid.has(folder)) setFolder(ALL);
  }, [fresh, byUuid, folder, setFolder]);
  // Inside a subject only its role decides whether a passage can be created there.
  const canCreateHere = currentSubject ? isAdmin || canCreateIn(currentSubject.role) : canCreate;

  const debouncedRef = useRef("");
  useEffect(() => {
    const id = setTimeout(() => {
      const next = search.trim();
      if (next === debouncedRef.current) return;
      debouncedRef.current = next;
      setDebouncedSearch(next);
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [search]);

  const params = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      subject: filterValue(subject),
      folder: filterValue(folder),
      ...(mine ? { author: myUuid } : {}),
    }),
    [debouncedSearch, subject, folder, mine, myUuid]
  );

  useEffect(() => {
    // Wait for the user: without the author My Questions would list everyone's passages.
    if (mine && !myUuid) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getPassages({ params: { page, page_size: PAGE_SIZE, ordering, ...params }, signal: controller.signal })
      .then((data) => {
        const rows = listOf(data);
        setResult({ rows, count: typeof data?.count === "number" ? data.count : rows.length });
        setLoading(false);
        markPassagesAvailable();
      })
      .catch((err) => {
        if (isCanceledError(err)) return;
        // The page is gone (its last passage was deleted): the one before it. On page 1 a 404 means an
        // old backend without passages.
        if (err?.response?.status === 404 && page > 1) {
          setPage((p) => Math.max(1, p - 1));
          return;
        }
        setError(err);
        setLoading(false);
      });
    return () => controller.abort();
  }, [params, ordering, page, reloadKey, mine, myUuid]);

  // ?open=<uuid>: the passage chips of the question tables link straight to their passage.
  useEffect(() => {
    if (openParam) setDialog({ open: true, uuid: openParam, folder: null });
  }, [openParam]);

  const closeDialog = () => {
    setDialog((d) => ({ ...d, open: false }));
    // Done with the link: a reload must not open the passage again.
    if (openParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("open");
          return next;
        },
        { replace: true }
      );
    }
  };

  const reload = () => setReloadKey((k) => k + 1);

  // The dialog closes itself after a save or a delete; the list is fetched again. A new passage heads the
  // default order (last updated first), so the list goes back to its first page.
  const handleSaved = () => {
    if (!dialog.uuid) setPage(1);
    reload();
  };

  const changeSubject = (option) => {
    setSubject(option?.value || ALL);
    setPage(1);
  };

  // Every filter back to "all" (the folder is where the user is, not a filter); the sort order stays.
  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    debouncedRef.current = "";
    setSubject(ALL);
    setPage(1);
  };

  const handleSort = (field) => {
    setOrdering((prev) => (prev === field ? `-${field}` : field));
    setPage(1);
  };

  // The way back to the Questions tab: the folder goes in the address (bank) and in the shared filters, which
  // the Questions tab falls back to (so "all" chosen here stays "all" there).
  const leaveTab = () => {
    setFilterState({ ...getFilterState(), folder });
  };

  const subjectOptions = useMemo(
    () => [
      { value: ALL, label: t("questions_page.filters.all_subjects") },
      ...subjects.map((s) => ({ value: s.uuid, label: s.name })),
    ],
    [subjects, t]
  );

  const sortOptions = [
    { value: "-updated", label: t("passages.sort.recent") },
    { value: "updated", label: t("passages.sort.oldest") },
    { value: "name", label: t("question_list.sort_options.name_asc") },
    { value: "-name", label: t("question_list.sort_options.name_desc") },
  ];

  const openPassage = (passage) => setDialog({ open: true, uuid: passage.uuid, folder: null });
  const openNew = (startFolder = null) => setDialog({ open: true, uuid: null, folder: startFolder });
  const showFolder = folder === ALL;

  const columns = useMemo(
    () => [
      {
        Header: t("passages.columns.name"),
        accessor: "name",
        accessor_for_ordering: "name",
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setDialog({ open: true, uuid: row.original.uuid, folder: null })}
            title={row.original.name}
            className={`inline-flex max-w-lg items-center gap-2 rounded text-left font-medium text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
          >
            <BookOpenIcon className="h-4 w-4 shrink-0 text-indigo-500" aria-hidden="true" />
            <span className="truncate">{row.original.name}</span>
          </button>
        ),
      },
      ...(currentSubject
        ? []
        : [
            {
              Header: t("question_list.columns.subject"),
              id: "subject",
              Cell: ({ row }) => <SubjectCell question={row.original} showFolder={showFolder} />,
            },
          ]),
      {
        Header: t("passages.columns.questions"),
        accessor: "questions_count",
        Cell: ({ value }) => (
          <span
            className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums ${
              value ? "bg-slate-100 text-slate-700" : "bg-slate-50 text-slate-400"
            }`}
          >
            {t("question_list.count", { count: value || 0 })}
          </span>
        ),
      },
      {
        Header: t("passages.columns.updated"),
        accessor: "updated",
        accessor_for_ordering: "updated",
        Cell: ({ value }) => <span className="tabular-nums text-slate-500">{formatDate(value, language)}</span>,
      },
    ],
    [t, language, currentSubject, showFolder]
  );

  const { rows, count } = result;
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const notAvailable = error?.response?.status === 404;
  const filtered = !!debouncedSearch || subject !== ALL;

  const renderList = () => {
    if (error) {
      return (
        <div role="alert" className={`${cardClass} flex flex-col items-center justify-center gap-3 px-6 py-16 text-center`}>
          <div className="rounded-full bg-red-50 p-3 text-red-500">
            <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-slate-900">{t("passages.load_failed")}</p>
          <button type="button" onClick={reload} className={linkButtonClass}>
            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
            {t("passages.panel.retry")}
          </button>
        </div>
      );
    }
    if (loading && rows.length === 0) {
      return (
        <div className={`${cardClass} flex items-center justify-center py-16`}>
          <div className="text-center">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
            <p className="text-sm text-slate-500">{t("table.loading")}</p>
          </div>
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className={`${cardClass} flex flex-col items-center justify-center px-6 py-16 text-center`}>
          <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
            {filtered ? (
              <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
            ) : (
              <BookOpenIcon className="h-6 w-6" aria-hidden="true" />
            )}
          </div>
          {filtered ? (
            <>
              <p className="text-sm text-slate-600">{t("passages.empty_filtered")}</p>
              <button
                type="button"
                onClick={clearFilters}
                className={`mt-4 inline-flex h-9 items-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 ${focusRing}`}
              >
                {t("question_list.clear_all")}
              </button>
            </>
          ) : (
            <>
              <h3 className="text-sm font-medium text-slate-900">{t("passages.empty_title")}</h3>
              {/* The "New passage" button is on screen already (the header's, or the subject card's). */}
              {canCreateHere && <p className="mt-1 max-w-md text-sm text-slate-500">{t("passages.empty_text")}</p>}
            </>
          )}
        </div>
      );
    }
    // A new page or filter keeps the current rows on screen, dimmed, until its rows arrive.
    return (
      <div className={`min-w-0 transition-opacity duration-150 ${loading ? "opacity-60" : ""}`}>
        {wide ? (
          <TableWithServerOrdering
            columns={columns}
            data={rows}
            ordering={ordering}
            handleOrdering={handleSort}
            hideCount
            dense
          />
        ) : (
          <ul className="flex flex-col gap-2.5">
            {rows.map((passage) => (
              <li
                key={passage.uuid}
                onClick={(event) => isOpenClick(event) && openPassage(passage)}
                className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition duration-200 hover:border-slate-300"
              >
                <button
                  type="button"
                  onClick={() => openPassage(passage)}
                  className={`flex max-w-full items-center gap-2 rounded text-left text-[15px] font-semibold leading-snug text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
                >
                  <BookOpenIcon className="h-4 w-4 shrink-0 text-indigo-500" aria-hidden="true" />
                  <span className="min-w-0 break-words">{passage.name}</span>
                </button>
                <p className="mt-1 text-sm text-slate-600">
                  {passage.folder ? folderPath(passage.folder) : passage.subject?.name || t("passages.no_folder")}
                </p>
                <div className="mt-2.5 flex items-center justify-between gap-3 text-sm text-slate-600">
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                    {t("question_list.count", { count: passage.questions_count || 0 })}
                  </span>
                  <span className="tabular-nums">{formatDate(passage.updated, language)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="min-w-0 space-y-5 motion-safe:animate-fade-in">
      {/* Title, actions and tab bar as on the Questions tab, so switching tabs does not move them. */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {/* The guide's "?" right after the title. */}
            <HelpTitle
              helpId="texts"
              helpTitle={t("help.texts.title")}
              guide={<TextsGuide canCreate={canCreate} mine={mine} buildsTests={canOpenPage(user, "/test_list")} />}
            >
              {mine ? t("my_questions.title") : t("nav.question_bank")}
            </HelpTitle>
            <p className="mt-1 text-sm text-slate-500">{mine ? t("my_questions.subtitle") : t("questions_page.subtitle")}</p>
          </div>
          {/* The one main button (phones: the whole row). Inside a subject the new passage is created from its card
              (FolderBar), in that subject. */}
          {canCreate && !notAvailable && !currentSubject && (
            <button type="button" onClick={() => openNew(null)} className={`${primaryButton} max-md:h-11 max-md:w-full`}>
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
              {t("passages.new")}
            </button>
          )}
        </div>
        <BankTabs
          active="texts"
          showTexts
          scope={mine ? "mine" : "bank"}
          search={!mine && folder !== ALL ? `?folder=${folder}` : ""}
          onLeave={leaveTab}
        />
        <p className="flex max-w-4xl items-start gap-2 text-sm text-slate-500">
          <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          {t(mine ? "passages.subtitle_mine" : "passages.subtitle")}
        </p>
      </div>

      {notAvailable ? (
        <div className={`${cardClass} flex flex-col items-center justify-center px-6 py-16 text-center`}>
          <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
            <BookOpenIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600">{t("passages.not_available")}</p>
        </div>
      ) : (
        <>
          {folders.length > 0 && (
            <FolderBar
              folders={folders}
              selected={folder}
              onSelect={setFolder}
              isAdmin={isAdmin}
              createLinks={false}
              showCounts={false}
              onCreate={canCreate ? (startFolder) => openNew(startFolder) : undefined}
              createLabel={t("passages.new")}
              allLabel={t("passages.all_texts")}
              onShare={(shared) => setShareDialog({ open: true, folder: shared })}
            />
          )}

          <FilterPanel
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={t("passages.search")}
            searchLabel={t("passages.search")}
            filters={
              currentSubject
                ? []
                : [
                    {
                      key: "subject",
                      active: subject !== ALL,
                      node: (
                        <Select
                          customStyles={filterSelectStyles}
                          ariaLabel={t("question_list.filter.subject")}
                          options={subjectOptions}
                          value={subject}
                          onChange={changeSubject}
                          placeholder={t("questions_page.filters.all_subjects")}
                          isSearchable
                        />
                      ),
                    },
                  ]
            }
            chips={
              currentSubject && subject !== ALL
                ? [
                    {
                      key: "subject",
                      label: t("question_list.filter.subject"),
                      value: optionLabel(subjectOptions, subject),
                      onRemove: () => changeSubject(null),
                    },
                  ]
                : []
            }
            canClear={filtered}
            onClearAll={clearFilters}
          />

          <ResultsBar
            countText={t("passages.count", { count })}
            page={page}
            pageCount={pageCount}
            ordering={ordering}
            onOrderingChange={(value) => {
              setOrdering(value);
              setPage(1);
            }}
            sortOptions={sortOptions}
            onPage={setPage}
            loading={loading}
          />

          <div className="min-w-0 overflow-hidden" aria-busy={loading}>
            {renderList()}
          </div>

          {rows.length > 0 && (
            <div className="flex justify-center">
              <Pager page={page} pageCount={pageCount} onPage={setPage} disabled={loading} label={t("question_list.pagination_bottom")} />
            </div>
          )}
        </>
      )}

      <PassageDialog
        isOpen={dialog.open}
        passageUuid={dialog.uuid}
        defaultFolder={dialog.folder}
        onClose={closeDialog}
        onSaved={handleSaved}
        onDeleted={reload}
      />
      <ShareFolderDialog
        isOpen={shareDialog.open}
        folder={shareDialog.folder}
        examName={byUuid.get(shareDialog.folder?.parent)?.name || ""}
        onClose={() => setShareDialog((d) => ({ ...d, open: false }))}
      />
    </div>
  );
}
